/* eslint-disable react/sort-comp */
/* jscs:disable requireCamelCaseOrUpperCaseIdentifiers */
/* jscs:disable maximumLineLength */
import React, { PureComponent } from 'react';
import { withTranslation, TFunction } from 'react-i18next';
import { connect } from 'react-redux';
import Select from 'react-select';
import styled, { css } from 'styled-components';
import SelectValue from './SelectValue';
import SelectOption from './SelectOption';
import { fontFamily, fontSize } from '../../constants/text';
import { BookingProfileStatus, LOCATION_SOURCE_CDP, SOURCE_BOOKING_WIDGET } from '../../constants';
import { colors } from '../../constants/colors';
import { isEmptyObject, safeGet } from '../../core/util/object';
import { isEmptyArray } from '../../core/util/array';
import {
  getDisplayableUserProfilesInAccountSummary,
  getFullNameFromSnakeCaseProps,
  getProfileFromAccountSummary,
} from '../../core/util/account';
import { getProfileColorTheme } from '../Account/util/colors';
import { emptyFunction } from '../../core/util/function';

const mapStateToProps = (state: any, ownProps: any) => {
  const newBookingProfileId = safeGet(state.newBooking)('profile.userProfileId');
  const accountProfile = newBookingProfileId
    ? getProfileFromAccountSummary(state.account.summary, newBookingProfileId)
    : null;
  const bookingProfile = safeGet(state.newBooking)('booking.userProfile.user_profile_id')
    ? state.newBooking.booking.userProfile
    : null;
  const defaultProfile = safeGet(state.account)('summary.user_profiles.0');
  return {
    userProfile: ownProps.userProfile || accountProfile || bookingProfile || defaultProfile,
  };
};

const PlaceHolder = styled.div.withConfig({ componentId: 'sc-fxyrpm-0' })<{ error?: boolean }>`
  height: 46px;
  box-sizing: border-box;
  text-indent: 22px;
  padding-top: 10px;
  font-size: ${fontSize.medium};
  font-family: ${fontFamily.book};
  border: 1px solid ${colors.grayvy};
  color: ${colors.squid};
  cursor: pointer;
  ${(props) => css`
    border: ${props.error && `2px solid ${colors.ladyInRed}`};
    color: ${props.error && colors.ladyInRed};
  `}
`;

const SelectArrow = styled.span.withConfig({ componentId: 'sc-fxyrpm-1' })`
  border-color: #999 transparent transparent;
  border-style: solid;
  border-width: 5px 5px 2.5px;
  display: inline-block;
  height: 0;
  width: 0;
  position: absolute;
  top: 22px;
  right: 20px;
`;

const Container = styled.div.withConfig({ componentId: 'sc-fxyrpm-2' })`
  position: relative;
  z-index: 1000;
  div[class='Select-menu-outer'] {
    background-color: white;
    box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14),
      0px 3px 14px 2px rgba(0, 0, 0, 0.12);
    position: absolute;
    top: 0;
    width: 100%;
    max-height: 300px;
    overflow-y: scroll;
    bottom: unset !important;
    left: unset !important;
  }
  div[class='Select-control'] {
    height: 55px;
    border-radius: 4px;
    span > div > div {
      padding-top: 8px;
      padding-bottom: 8px;
    }
  }

  div[class='Select-input'] {
    display: block !important;
    height: 0;
    padding: 0;
  }
`;

const StyledSelect = styled(Select).withConfig({ componentId: 'sc-fxyrpm-3' })`
  height: 46px;
`;

const renderArrow = () => <SelectArrow />;

type OwnUserProfileSelectorProps = {
  accountSummary?: any;
  hasError?: boolean;
  defaultNotSelected?: boolean;
  className?: string;
  handleChangeUserProfile?: (...args: any[]) => any;
  userProfile?: any;
  source?: string;
  t: TFunction<'common'>;
};

type UserProfileSelectorState = any;

type UserProfileSelectorProps = OwnUserProfileSelectorProps &
  typeof UserProfileSelector.defaultProps;

class UserProfileSelector extends PureComponent<
  UserProfileSelectorProps,
  UserProfileSelectorState
> {
  static defaultProps = {
    handleChangeUserProfile: emptyFunction,
  };

  getDefaultOption = () => {
    const options = this.getSelectorOptions();
    if (!isEmptyArray(options)) {
      const activeUserProfileId = safeGet(this.props)('userProfile.user_profile_id');
      if (activeUserProfileId) {
        const activeOptions = options.filter((o: any) => o.value === activeUserProfileId);
        if (activeOptions.length > 0) {
          this.props.handleChangeUserProfile(activeOptions[0]);
          return activeOptions[0];
        }
      }

      this.props.handleChangeUserProfile(options[0]);
      return options[0];
    }

    return null;
  };

  getSelectorOption = (userProfile: any, selectedOption: any) => {
    const { accountSummary } = this.props;
    if (!isEmptyObject(userProfile) && !isEmptyObject(userProfile.user_profile_id)) {
      const obj = {
        key: userProfile.user_profile_id,
        label: getFullNameFromSnakeCaseProps(userProfile),
        value: userProfile.user_profile_id,
        color: getProfileColorTheme(accountSummary, userProfile.user_profile_id),
        type: BookingProfileStatus.EXISTS,
      };
      return {
        ...obj,
        isSelected: !isEmptyObject(selectedOption) && selectedOption.key === obj.key,
      };
    }

    return null;
  };

  getSelectorOptions = () => {
    const { t } = this.props;
    const userProfiles = getDisplayableUserProfilesInAccountSummary(this.props.accountSummary);
    if (isEmptyArray(userProfiles)) {
      return null;
    }

    const selectedOption = safeGet(this.state)('selectedOption');
    const selectorOptions = userProfiles.map((profile: any) => {
      return this.getSelectorOption(profile, selectedOption);
    });

    if (
      this.props?.source === LOCATION_SOURCE_CDP ||
      this.props?.source === SOURCE_BOOKING_WIDGET
    ) {
      const addNewUserObj = {
        key: 'addNewFamilyMember',
        label: t('addNewFamilyMember'),
        value: '',
        isSelected:
          !isEmptyObject(selectedOption) && selectedOption.type === BookingProfileStatus.PENDING,
        type: BookingProfileStatus.PENDING,
      };
      selectorOptions.push(addNewUserObj);
    }

    return selectorOptions;
  };

  state = {
    selectedOption: this.props.defaultNotSelected ? null : this.getDefaultOption(),
  };

  handleChange = (selectedOption: any) => {
    this.setState({ selectedOption });
    this.props.handleChangeUserProfile(selectedOption);
  };

  render() {
    const { selectedOption } = this.state;
    const { hasError, className } = this.props;
    const options = this.getSelectorOptions();
    if (isEmptyArray(options)) {
      return null;
    }

    return (
      <Container className={className} id="user-profile-container-selector">
        <StyledSelect
          arrowRenderer={renderArrow}
          autoBlur
          clearable={false}
          id="user-profile-selector"
          multi={false}
          onBlur={emptyFunction}
          onChange={this.handleChange}
          onSelect={emptyFunction}
          optionComponent={SelectOption}
          options={options}
          placeholder={<PlaceHolder error={hasError}>Select profile ...</PlaceHolder>}
          searchable={false}
          tabSelectsValue
          value={selectedOption}
          valueComponent={SelectValue}
        />
      </Container>
    );
  }
}

export default connect(mapStateToProps)(withTranslation()(UserProfileSelector));
